import { EVENT_GLOBAL_UPDATE_CART } from '../constants/event';
import { CartAddItem } from '../typs/cart';

class Cart {
  private static _count: number | null = null;
  private static _items: CartAddItem[] = [];
  private static _isUpdating: boolean = false;

  static get count(): number {
    if (this._count === null && !this._isUpdating) {
      this._isUpdating = true;
      this.updateCount().finally(() => {
        this._isUpdating = false;
      });
      return 0;
    }
    return this._count ?? 0;
  }

  static get items(): CartAddItem[] {
    return this._items;
  }

  private static async updateCount(count: number | null = null): Promise<void> {
    try {
      if (count === null) {
        const response = await fetch('/cart.js', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error('Network response was not ok');
        }

        const cartData = await response.json();
        this._count = cartData.item_count;
      } else {
        this._count = count;
      }
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }

  private static async updateItems(
    items: CartAddItem[] | null = null
  ): Promise<void> {
    try {
      if (items === null) {
        const response = await fetch('/cart.js', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error('Network response was not ok');
        }

        const cartData = await response.json();
        this._items = cartData.items;
      } else {
        this._items = items;
      }
    } catch (error) {
      console.error('Error updating cart items:', error);
    }
  }

  static async add(items: CartAddItem | CartAddItem[]): Promise<any> {
    const itemsArray = Array.isArray(items) ? items : [items];
    this._items = itemsArray;
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items: itemsArray }),
      });
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const result = await response.json();
      await this.updateCount(result.item_count);
      await this.updateItems(result.items);
      document.dispatchEvent(new CustomEvent(EVENT_GLOBAL_UPDATE_CART));
      return result;
    } catch (error) {
      console.error('Error adding item to cart:', error);
      throw error;
    }
  }

  static async get(): Promise<any> {
    try {
      const response = await fetch('/cart.js', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const result = await response.json();
      await this.updateCount(result.item_count);
      await this.updateItems(result.items);
      return result;
    } catch (error) {
      console.error('Error getting cart:', error);
      throw error;
    }
  }

  static async update(items: CartAddItem | CartAddItem[]): Promise<any> {
    const itemsArray = Array.isArray(items) ? items : [items];
    try {
      // updates オブジェクトを作成
      const updates: { [key: string]: number } = {};
      itemsArray.forEach((item) => {
        if (item.id && item.quantity !== undefined && item.quantity !== null) {
          updates[item.id] = item.quantity;
        }
      });

      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ updates }), // updates オブジェクトを使用
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const result = await response.json();
      await this.updateCount(result.item_count);
      await this.updateItems(result.items);
      document.dispatchEvent(new CustomEvent(EVENT_GLOBAL_UPDATE_CART));
      return result;
    } catch (error) {
      console.error('Error updating cart:', error);
      throw error;
    }
  }

  static async remove(items: CartAddItem | CartAddItem[]): Promise<any> {
    const itemsArray = Array.isArray(items) ? items : [items];
    try {
      const updates = itemsArray.reduce(
        (acc: { [key: string]: number }, item) => {
          acc[item.id] = 0;
          return acc;
        },
        {}
      );

      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ updates }),
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const result = await response.json();
      await this.updateCount(result.item_count);
      await this.updateItems(result.items);
      document.dispatchEvent(new CustomEvent(EVENT_GLOBAL_UPDATE_CART));
      return result;
    } catch (error) {
      console.error('Error removing item from cart:', error);
      throw error;
    }
  }

  static async getCount(): Promise<number> {
    try {
      const response = await fetch('/cart.js', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const cartData = await response.json();
      return cartData.item_count;
    } catch (error) {
      console.error('Error getting cart count:', error);
      throw error;
    }
  }
}

export default Cart;
